/**
 * Setstart-bevestiging: NIET stilzwijgend aannemen wie begint.
 * Vraagt per set: eerste serveerteam, eerste serveerder per team (volgorde),
 * en de fysieke startzijden. Vooraf ingevuld met de setup-waarden.
 */

import { h } from "./dom";
import type { App, SetPlan } from "./app";

export function openSetConfirm(app: App, setNumber: number, onDone: () => void): void {
  const plan: SetPlan = structuredClone(app.config.setPlans[Math.min(setNumber, 3) - 1] ?? defaults(setNumber));
  const overlay = h("div", { class: "modal-backdrop" });
  const teams = app.resolveConfig().teams;

  const firstServeSel = h(
    "select",
    {},
    [
      h("option", { value: "A", selected: plan.firstServeTeam === "A" }, [teams.A.name]),
      h("option", { value: "B", selected: plan.firstServeTeam === "B" }, [teams.B.name]),
    ],
  );

  function inOrderSelect(team: "A" | "B"): HTMLSelectElement {
    const sel = h(
      "select",
      {},
      [
        h("option", { value: "0", selected: plan.firstInOrder[team] === 0 }, [teams[team].players[0]]),
        h("option", { value: "1", selected: plan.firstInOrder[team] === 1 }, [teams[team].players[1]]),
      ],
    );
    sel.addEventListener("change", () => {
      plan.firstInOrder[team] = (Number(sel.value) as 0 | 1);
    });
    return sel;
  }

  const sideSel = h(
    "select",
    {},
    [
      h("option", { value: "A-left", selected: plan.startSides.A === "left" }, [`${teams.A.name} links`]),
      h("option", { value: "A-right", selected: plan.startSides.A === "right" }, [`${teams.A.name} rechts`]),
    ],
  );
  sideSel.addEventListener("change", () => {
    const aLeft = sideSel.value === "A-left";
    plan.startSides = { A: aLeft ? "left" : "right", B: aLeft ? "right" : "left" };
    updateSidePreview();
  });

  const sidePreview = h("span", { class: "side-preview" });
  function updateSidePreview(): void {
    sidePreview.textContent = `↔ ${plan.startSides.A === "left" ? teams.A.name : teams.B.name} staat links aan de camera`;
  }
  updateSidePreview();

  const title = h("h2", {}, [`Bevestig set ${setNumber}`]);
  const hint = h("p", { class: "muted" }, [
    "Dit wordt NIET stilzwijgend aangenomen — bevestig alstublieft wie deze set begint en met welke spelers.",
  ]);

  const body = h("div", { class: "modal" }, [
    title,
    hint,
    h("label", { class: "field" }, [
      "Eerste serveerteam",
      firstServeSel,
    ]),
    h("label", { class: "field" }, [
      `Eerste serveerder t.o.v. volgorde — ${teams.A.name}`,
      inOrderSelect("A"),
    ]),
    h("label", { class: "field" }, [
      `Eerste serveerder t.o.v. volgorde — ${teams.B.name}`,
      inOrderSelect("B"),
    ]),
    h("label", { class: "field" }, ["Startzijde (beoordeling camera)", sideSel]),
    sidePreview,
    h("div", { class: "btn-row" }, [
      h("button", { class: "btn ghost", onclick: () => overlay.remove() }, ["Annuleren"]),
      h(
        "button",
        {
          class: "btn primary",
          onclick: () => {
            plan.firstServeTeam = firstServeSel.value as "A" | "B";
            app.config.setPlans[Math.min(setNumber, 3) - 1] = structuredClone(plan);
            overlay.remove();
            app.startSet(setNumber, plan);
            onDone();
          },
        },
        ["Start set " + setNumber],
      ),
    ]),
  ]);

  overlay.append(body);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) overlay.remove();
  });
  document.body.append(overlay);
}

function defaults(setNumber: number): SetPlan {
  return {
    firstServeTeam: setNumber === 2 ? "B" : "A",
    firstInOrder: { A: 0, B: 0 },
    startSides: setNumber === 2 ? { A: "right", B: "left" } : { A: "left", B: "right" },
  };
}
